import React, { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BodyText, H1, SmallText } from '../ui/Typography';
import ActionButtons from './ActionButtons';
import { InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import SiteMapModel from '../../steps/Sitemap/models/SiteMapModel';
import BusinessModel from '../../steps/FindYourBusiness/models/BusinessModel';
import EditPageModal from './EditPageModal';
import InternalSitemapTree from './InternalSitemapTree';
import ExternalSitemapTree from './ExternalSitemapTree';
import { 
  DndContext, 
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  DragOverEvent,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
  UniqueIdentifier
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import useSavePageStructure from './hooks/useSavePageStructure';
import PreparingWebsitePreview from '../PreparingWebsitePreview';
import SiteBuilderModel from '../../steps/SiteBuilder/model/SiteBuilderModel';
import { getProjection, flattenTree, getChildCount } from './utilities';
import InternalTreeNode from './components/InternalTreeNode';
import ExternalTreeNode from './components/ExternalTreeNode';
import { FolderOpenOutlined } from '@ant-design/icons';

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const INDENTATION_WIDTH = 20;

const ManageSitemap: React.FC<{ onCancel?: () => void }> = observer(
  ({ onCancel }) => {
    const { t } = useTranslation();
    const savePageStructure = useSavePageStructure();
    
    // Drag state
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
    const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
    const [offsetLeft, setOffsetLeft] = useState(0);
    const [dragRevision, setDragRevision] = useState(0);
    const [overRect, setOverRect] = useState<DOMRect | null>(null);
    const [activeRect, setActiveRect] = useState<DOMRect | null>(null);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5,
        },
      })
    );

    const handleSave = async () => {
      SiteMapModel.setSaving(true);
      try {
        await savePageStructure();
        SiteBuilderModel.setIframeLoaded(false);
        const waitForIframe = () =>
          new Promise<void>((resolve) => {
            const checkLoaded = () => {
              if (SiteBuilderModel.iframeLoaded) {
                resolve();
              } else {
                setTimeout(checkLoaded, 200);
              }
            };
            checkLoaded();
          });

        await waitForIframe();

        SiteMapModel.setSaving(false);
        SiteMapModel.clearManagePagesSnapshot();
        SiteMapModel.closeManagePages();

        // scroll the main page to the top so the user sees the refreshed preview.
        try {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } catch (e) {
          // fallback for older browsers
          window.scrollTo(0, 0);
        }
      } catch (error) {
        SiteMapModel.setSaving(false);
      }
    };

    const SITEMAP_ROOT_INTERNAL = SiteMapModel.SITEMAP_ROOT_INTERNAL;

    const websiteForSitemap =
      BusinessModel.selectedBusinessWebsite || SiteMapModel.websiteUrl || '';

    const indexPage = SiteMapModel.treeSiteMap.find(
      (node) => node.data.type === 'Startseite'
    );

    const otherPages = SiteMapModel.treeSiteMap.filter(
      (node) =>
        node.data.type !== 'Startseite' && node.parent === SITEMAP_ROOT_INTERNAL
    );
    
    // Sort other pages
    const isDefaultRoot = (node: any) => node.data.type === 'default';
    const sortedOtherPages = [
      ...otherPages
        .filter(node => !isDefaultRoot(node))
        .sort((a, b) => {
          if (a.data.order !== undefined && b.data.order !== undefined) {
            return a.data.order - b.data.order;
          }
          if (a.data.order !== undefined) return -1;
          if (b.data.order !== undefined) return 1;
          return 0;
        }),
      ...otherPages.filter(isDefaultRoot)
    ];

    // Flatten tree for projection calculation
    const flattenedItems = useMemo(() => {
      return flattenTree(sortedOtherPages, SiteMapModel.treeSiteMap);
    }, [sortedOtherPages, SiteMapModel.treeSiteMap]);

    const projected = useMemo(() => {
      if (!activeId || !overId || !activeRect || !overRect) return null;
      
      const activeNode = SiteMapModel.treeSiteMap.find((n) => n.id === activeId);
      const overNode = SiteMapModel.treeSiteMap.find((n) => n.id === overId);
      
      // If dragging external item, only calculate projection if over an internal node
      if (activeNode?.data.origin === 'external') {
        if (!overNode || overNode.data.origin === 'external') {
          return null;
        }
      }
      
      // For external items, add them temporarily to calculate projection
      let itemsForProjection = [...flattenedItems];
      if (activeNode?.data.origin === 'external' && overNode) {
        const overIndex = itemsForProjection.findIndex(item => item.id === overId);
        if (overIndex !== -1) {
          itemsForProjection.splice(overIndex + 1, 0, {
            ...activeNode,
            depth: 0,
            index: overIndex + 1
          });
          itemsForProjection.forEach((item, idx) => {
            item.index = idx;
          });
        }
      }
      
      // Calculate offset relative to the container, not the initial drag point
      const isExternal = activeNode?.data.origin === 'external';
      const relativeOffsetLeft = isExternal 
        ? activeRect.left - overRect.left 
        : offsetLeft;

      return getProjection(
        itemsForProjection,
        activeId,
        overId,
        relativeOffsetLeft,
        INDENTATION_WIDTH
      );
    }, [activeId, overId, flattenedItems, offsetLeft, SiteMapModel.treeSiteMap, activeRect, overRect]);

    // Search for active item in entire treeSiteMap (not just flattenedItems) 
    // to support dragging external pages
    const activeItem = activeId
      ? SiteMapModel.treeSiteMap.find((node) => node.id === activeId)
      : null;

    function handleDragStart({ active }: DragStartEvent) {
      setActiveId(active.id);
      setOverId(active.id);
      document.body.style.setProperty('cursor', 'grabbing');
    }

    function handleDragMove({ delta, active, over }: DragMoveEvent) {
      setOffsetLeft(delta.x);
      if (over?.rect) {
        // Wrap as DOMRect to satisfy state typing
        setOverRect(new DOMRect(over.rect.left, over.rect.top, over.rect.width, over.rect.height));
      }
      if (active?.rect) {
        const tr = active.rect.current.translated;
        if (tr) {
          setActiveRect(new DOMRect(tr.left, tr.top, tr.width, tr.height));
        }
      }
    }

    function handleDragOver({ over }: DragOverEvent) {
      // Don't allow hovering over default pages
      if (over) {
        const overNode = SiteMapModel.treeSiteMap.find((n) => n.id === over.id);
        if (overNode && (overNode.data.type === 'default' || overNode.data.type === 'Startseite')) {
          setOverId(null);
          return;
        }
      }
      setOverId(over?.id ?? null);
    }

    function handleDragEnd(event: DragEndEvent) {
      const { active, over } = event;
      
      const draggedNode = SiteMapModel.treeSiteMap.find(
        (n) => n.id === active.id
      );
      
      if (!draggedNode) {
        resetDragState();
        return;
      }

      // If external page dropped outside internal tree, don't move it
      const isExternalNode = draggedNode.data.origin === 'external';
      
      // For external nodes, only allow drop if over is explicitly an internal node
      if (isExternalNode) {
        // If no drop target or dropped on external area, keep it in external
        if (!over || over.id === 'external-tree-droppable') {
          resetDragState();
          return;
        }
        
        const overNode = SiteMapModel.treeSiteMap.find((n) => n.id === over.id);
        if (!overNode || overNode.data.origin === 'external') {
          resetDragState();
          return; // Don't move if dropped over external or nowhere
        }
        
        // Prevent dropping on or between default pages
        if (overNode.data.type === 'default' || overNode.data.type === 'Startseite') {
          resetDragState();
          return;
        }
        
        // Only allow drop over internal/custom pages
        const isOverInternal = 
          overNode.data.origin === 'internal' || 
          overNode.data.origin === 'custom' ||
          overNode.parent === SITEMAP_ROOT_INTERNAL;
        
        if (!isOverInternal) {
          resetDragState();
          return;
        }
      }
      
      // For non-external nodes, require a valid drop target
      if (!over) {
        resetDragState();
        return;
      }

      if (!projected) {
        resetDragState();
        return;
      }

      // Prevent dragging locked pages
      if (draggedNode.data?.type === 'Startseite' || draggedNode.data?.type === 'default') {
        resetDragState();
        return;
      }
      
      // Prevent dropping ANY page on or between default pages
      const overNode = SiteMapModel.treeSiteMap.find((n) => n.id === over.id);
      if (overNode && (overNode.data.type === 'default' || overNode.data.type === 'Startseite')) {
        resetDragState();
        return;
      }
      
      if (!draggedNode.data?.canDrag) {
        resetDragState();
        return;
      }

      const { depth, parentId } = projected;
      
      // Clone the array to avoid mutation issues
      const clonedItems = [...SiteMapModel.treeSiteMap];
      
      // Find indices
      const overIndex = clonedItems.findIndex((n) => n.id === over.id);
      const activeIndex = clonedItems.findIndex((n) => n.id === active.id);
      
      if (overIndex === -1 || activeIndex === -1) {
        resetDragState();
        return;
      }

      // Update parent and depth
      const newParent = parentId === null ? SITEMAP_ROOT_INTERNAL : parentId;
      clonedItems[activeIndex] = {
        ...clonedItems[activeIndex],
        parent: newParent
      };

      // Reorder array using arrayMove
      const reorderedItems = arrayMove(clonedItems, activeIndex, overIndex);

      // Update external origin to internal if needed
      const movedItem = reorderedItems.find((n) => n.id === active.id);
      if (movedItem && movedItem.data?.origin === 'external') {
        movedItem.data.origin = 'internal';
        movedItem.data.canEdit = true;
        movedItem.data.canDelete = true;
        movedItem.droppable = true;
        movedItem.data.movedFromExternal = true;
      }

      // Recompute order for all siblings under each unique parent
      const uniqueParents = [...new Set(reorderedItems.map(n => n.parent))];
      uniqueParents.forEach(parent => {
        const siblings = reorderedItems.filter((n) => n.parent === parent);
        siblings.forEach((sibling, index) => {
          sibling.data.order = index;
        });
      });

      SiteMapModel.updateSitemap(reorderedItems);
      
      // Reset drag state only after successful update
      resetDragState();
    }

    function handleDragCancel() {
      resetDragState();
    }

    function resetDragState() {
      setOverId(null);
      setActiveId(null);
      setOffsetLeft(0);
      setOverRect(null);
      setActiveRect(null);
      setDragRevision(prev => prev + 1);
      document.body.style.setProperty('cursor', '');
    }


    return (
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="manage-sitemap-container h-full">
          {SiteMapModel.isSaving ? (
            <PreparingWebsitePreview />
          ) : (
            <>
              {/* ---------- HEADER ---------- */}
              <div className="headline-section flex justify-between px-[178px] py-[48px]">
                <div className="headline">
                  <H1 className="headline">
                    {t('Edit the structure of your page')}
                  </H1>

                  <div className="below-headline-text flex items-start gap-2">
                    <InfoCircleOutlined
                      className="text-gray-600 mt-1"
                      style={{ fontSize: 16 }}
                    />
                    <div>
                      <SmallText className="instruction">
                        <Trans
                          i18nKey="pagesTree.sitemapDescription"
                          defaults="This sitemap was generated for you based on your company data and industry. <b>You can customize your site structure at any time after completing your purchase.</b>"
                          components={{ b: <b /> }}
                        />
                      </SmallText>
                    </div>
                  </div>
                </div>

                <div className="buttons">
                  <ActionButtons onSave={handleSave} onCancel={onCancel} />
                </div>
              </div>

              {/* ---------- TREES ---------- */}
              <div className="sitemaps px-[178px] py-[48px] bg-[#F5F5F5] flex gap-[48px]">
                <div className="flex-[0_0_55%]">
                  <div
                    className="main-sitemap px-6 pt-6 pb-[26px] rounded-xl bg-white"
                    style={{
                      boxShadow:
                        '0 1px 2px -2px rgba(0,0,0,0.16), 0 3px 6px 0 rgba(0,0,0,0.12), 0 5px 12px 4px rgba(0,0,0,0.09)'
                    }}>
                    <div>
                      {indexPage && (
                        <div
                          className="index-page-container border border-[rgba(0,0,0,0.15)]"
                          style={{
                            borderLeft:
                              '1px solid var(--colorBorder, rgba(0, 0, 0, 0.15))',
                            borderRight:
                              '1px solid var(--colorBorder, rgba(0, 0, 0, 0.15))'
                          }}>
                          <InternalSitemapTree
                            nodes={[indexPage]}
                            allNodes={SiteMapModel.treeSiteMap}
                            root={SITEMAP_ROOT_INTERNAL}
                            activeId={null}
                            projected={null}
                            indentationWidth={INDENTATION_WIDTH}
                          />
                        </div>
                      )}

                      <div className="separator bg-[rgba(0,0,0,0.06)] h-[1px] my-[12px]" />

                      <div
                        className="other-pages"
                        style={{
                          border:
                            '1px solid var(--colorBorder, rgba(0, 0, 0, 0.15))',
                          borderBottom: 'none'
                        }}>
                        <InternalSitemapTree
                          nodes={sortedOtherPages}
                          allNodes={SiteMapModel.treeSiteMap}
                          root={SITEMAP_ROOT_INTERNAL}
                          activeId={activeId}
                          projected={projected}
                          indentationWidth={INDENTATION_WIDTH}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {(() => {
                  const website = BusinessModel.selectedBusinessWebsite || '';
                  const domainType =
                    BusinessModel.selectedBusinessDomainType || '';
                  const isFacebook =
                    website.startsWith('http://www.facebook.com') ||
                    website.startsWith('https://www.facebook.com');
                  const isNoDomain = domainType === 'user_wants_website_domain';
                  if (!isFacebook && !isNoDomain) {
                    return (
                      <div className="right-container flex-[0_0_45%]">
                        <BodyText>
                          <Trans
                            i18nKey="sitemap_description"
                            components={{ b: <b />, br: <br /> }}
                            values={{ websiteUrl: websiteForSitemap }}
                          />
                        </BodyText>
                        <ExternalSitemapTree key={dragRevision} />
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div className="buttons-holder px-[178px] py-[48px]">
                <ActionButtons onSave={handleSave} onCancel={onCancel} />
              </div>

              <EditPageModal
                visible={SiteMapModel.isEditPageModalOpened}
                onCancel={() => SiteMapModel.closeEditPageModal()}
                onSave={(data) => {
                  if (SiteMapModel.editedPageNode) {
                    SiteMapModel.editPage({
                      ...data,
                      shouldRegenerateContent: data.regenerate
                    });
                  } else {
                    SiteMapModel.addCustomPage({
                      pageName: data.pageName,
                      description: data.description,
                      parentPageId:
                        SiteMapModel.parentNodeForNewPage?.id ||
                        SiteMapModel.SITEMAP_ROOT_INTERNAL,
                      keywords: data.customKeywords,
                      insertAfterId:
                        SiteMapModel.parentInsertionReference?.id || null,
                      shouldRegenerateContent: data.regenerate
                    });
                  }
                  SiteMapModel.closeEditPageModal();
                }}
                initialData={
                  SiteMapModel.editedPageNode
                    ? {
                      id: SiteMapModel.editedPageNode.id,
                      pageName: SiteMapModel.editedPageNode.text,
                      description:
                        SiteMapModel.editedPageNode.data?.description || '',
                      customKeywords:
                        SiteMapModel.editedPageNode.data?.customKeywords ||
                        [],
                      parentPageId: SiteMapModel.editedPageNode.parent,
                      isDefault:
                        SiteMapModel.editedPageNode.data?.isPageDefault,
                      isNew: false
                    }
                    : {
                      id: null,
                      pageName: '',
                      description: '',
                      customKeywords: [],
                      parentPageId:
                        SiteMapModel.parentNodeForNewPage?.id ||
                        SiteMapModel.SITEMAP_ROOT_INTERNAL,
                      isDefault: false,
                      isNew: true
                    }
                }
                mode={SiteMapModel.editedPageNode ? 'edit' : 'add'}
              />
            </>
          )}
        </div>
        
        {/* Drag Overlay - must be inside DndContext */}
        <DragOverlay dropAnimation={null}>
          {activeId && activeItem ? (
            <div
              style={{
                backgroundColor: '#E6F7FF',
                color: '#000000',
                border: '1px solid rgba(0, 0, 0, 0.15)',
                cursor: 'grabbing',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
                borderRadius: '6px',
                minWidth: '300px',
                maxWidth: '500px'
              }}
            >
              {/* Parent item */}
              <div
                style={{
                  padding: projected?.depth 
                    ? `16px 24px 16px 10px`
                    : '16px 24px',
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '24px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                className="flex items-center gap-2"
              >
                {(() => {
                  const hasChildren = SiteMapModel.treeSiteMap.filter(child => child.parent === activeItem.id).length > 0;
                  if (hasChildren) {
                    return <FolderOpenOutlined style={{ fontSize: '16px', color: '#000000' }} />;
                  } else if (projected?.depth > 0) {
                    return <MoreOutlined style={{ fontSize: '16px', color: '#000000' }} />;
                  }
                  return null;
                })()}
                {activeItem.text}
              </div>
              
              {/* Render children */}
              {(() => {
                const children = SiteMapModel.treeSiteMap.filter(child => child.parent === activeItem.id);
                if (children.length === 0) return null;
                
                const renderChild = (child: any, childDepth: number): React.ReactNode => {
                  const grandChildren = SiteMapModel.treeSiteMap.filter(gc => gc.parent === child.id);
                  const hasChildChildren = grandChildren.length > 0;
                  const baseDepth = projected?.depth || 0;
                  const childIndent = 24 + (baseDepth + childDepth) * INDENTATION_WIDTH;
                  
                  return (
                    <div key={child.id}>
                      <div 
                        style={{ 
                          paddingLeft: `${childIndent}px`,
                          paddingRight: '24px',
                          paddingTop: '12px',
                          paddingBottom: '15px',
                          color: 'rgba(0, 0, 0, 0.65)',
                          fontSize: '14px',
                          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        className="flex items-center gap-2"
                      >
                        {hasChildChildren ? (
                          <FolderOpenOutlined style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }} />
                        ) : (
                          <MoreOutlined style={{ fontSize: '14px' }} />
                        )}
                        {child.text}
                      </div>
                      {grandChildren.map(gc => renderChild(gc, childDepth + 1))}
                    </div>
                  );
                };
                
                return <>{children.map(child => renderChild(child, 1))}</>;
              })()}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }
);

export default ManageSitemap;
